import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { FieldWrapper, fieldToneClasses } from './Input';

export interface ComboboxOption {
  id: string;
  label: string;
}

interface ComboboxProps {
  label?: string;
  options: ComboboxOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  error?: string | null;
  warning?: string | null;
  /**
   * Roher, nicht zugeordneter Text (z.B. ein OCR-erkannter Container-Code
   * ohne Stammdaten-Treffer) - wird angezeigt/als Sucheingabe vorbelegt,
   * solange kein echter Treffer ausgewählt ist, damit die Person sieht, WAS
   * erkannt wurde, statt eines leeren Felds ohne jeden Anhaltspunkt.
   */
  unmatchedText?: string | null;
  disabled?: boolean;
}

/**
 * Autocomplete-Feld für Container/Material/Legierung/Fahrer (Anforderung:
 * "Autocomplete") - Tippen filtert die Liste, Pfeiltasten/Enter wählen aus.
 * Ersetzt das jQuery-UI-Autocomplete-Widget des Altsystems durch eine
 * schlanke, abhängigkeitsfreie React-Implementierung.
 */
export function Combobox({ label, options, value, onChange, placeholder, error, warning, unmatchedText, disabled }: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.id === value)?.label ?? '';
  const showingUnmatchedHint = !open && !value && !!unmatchedText;
  const closedDisplayValue = value ? selectedLabel : (unmatchedText ?? '');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  function select(option: ComboboxOption) {
    onChange(option.id);
    setOpen(false);
  }

  return (
    <FieldWrapper label={label} error={error} warning={warning ?? (showingUnmatchedHint ? 'Nicht in den Stammdaten gefunden - bitte auswählen oder anlegen.' : null)}>
      <div ref={rootRef} className="relative">
        <input
          disabled={disabled}
          value={open ? query : closedDisplayValue}
          placeholder={placeholder}
          onFocus={() => {
            setOpen(true);
            // Rohtext als Sucheingabe vorbelegen, damit die Trefferliste
            // direkt passende Stammdaten zeigt statt einer leeren Suche.
            if (!value && unmatchedText && query === '') setQuery(unmatchedText);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
            setOpen(true);
            if (e.target.value === '') onChange(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const opt = filtered[highlight];
              if (opt) select(opt);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          className={clsx(
            'w-full rounded-md border px-3 py-2 text-sm placeholder:text-ivory-500 outline-none transition-colors focus:border-accent-400 focus:ring-2 focus:ring-accent-500/20 disabled:opacity-50',
            showingUnmatchedHint ? 'italic text-warning-400' : 'text-ivory-100',
            fieldToneClasses(error, warning ?? showingUnmatchedHint),
          )}
        />
        {open && filtered.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-surface-500 bg-surface-800 py-1 shadow-popover">
            {filtered.map((option, i) => (
              <li
                key={option.id}
                onMouseDown={() => select(option)}
                className={clsx('cursor-pointer px-3 py-1.5 text-sm', i === highlight ? 'bg-accent-500/15 text-accent-400' : 'text-ivory-200 hover:bg-surface-700')}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </FieldWrapper>
  );
}
